<template>
  <div class="grid-view">
    <!-- Loading State -->
    <div
      v-if="isListLoading && list.length === 0"
      class="loader-container"
    >
      <Loader />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="list.length === 0 && !isListLoading"
      class="empty-message"
    >
      <i class="far fa-folder-open fa-3x text-muted mb-3" />
      <p class="text-muted">
        {{ t('templateSelector.list.empty') }}
      </p>
    </div>

    <!-- Templates Grid -->
    <div
      v-else
      ref="scrollContainer"
      class="scroll-container"
      @scroll="handleScroll"
    >
      <div class="grid flex flex-grow-1 justify-content-start">
        <div
          v-for="record of list"
          :key="record.id_template"
          class="ctx xl:col-4 lg:col-4 md:col-6 sm:col-12 mb-3 p-4"
        >
          <div class="preview-label mb-2">
            <div class="template-title-container">
              <i
                v-if="record.name_marketing_workflow"
                v-tooltip="{ value: t('templateBuilder.saveSyncElementModal.fromAutomatedScenario') }"
                class="far fa-share-nodes template-source-icon automated-scenario-icon"
              />
              <i
                v-else
                v-tooltip="{ value: t('templateBuilder.saveSyncElementModal.fromMyTemplates') }"
                class="far fa-table template-source-icon my-templates-icon"
              />
              <span
                v-tooltip="{ value: record.label }"
                class="template-title"
              >
                {{ record.label }}
              </span>
            </div>
            <div class="template-scenario-subtitle">
              <span v-if="record.name_marketing_workflow">{{ record.name_marketing_workflow }}</span>
              <span v-else>&nbsp;</span>
            </div>
            <!-- Section selection indicator -->
            <div
              v-if="isElementPartTypeSection && selectedTemplates.has(record.id_template)"
              class="selected-sections-indicator"
            >
              <i class="far fa-layer-group mr-1" />
              <span class="sections-count">
                {{ getSelectedSectionsForTemplate(record.id_template).length }} {{ t('templateBuilder.saveSyncElementModal.sectionsSelected', { count: getSelectedSectionsForTemplate(record.id_template).length }) }}
              </span>
            </div>
          </div>

          <div
            class="preview-container-template"
            :class="{
              'selected': selectedTemplates.has(record.id_template),
              'section-mode': isElementPartTypeSection
            }"
            @click="toggleSelection(record.id_template)"
          >
            <div class="selection-checkbox">
              <input
                :id="`checkbox-${record.id_template}`"
                type="checkbox"
                :checked="selectedTemplates.has(record.id_template)"
                class="template-checkbox"
                @change="toggleSelection(record.id_template)"
              >
              <label
                :for="`checkbox-${record.id_template}`"
                class="checkbox-label"
              />
            </div>
            <img
              :src="`${baseUrl + record.imageKey}.png&w=400&${new Date().getTime()}`"
              style="display: none"
              @load="imageLoaded(record.id_template)"
              @error="imageFailed(record.id_template)"
            >
            <div
              v-if="imageLoadTime[record.id_template] && imageLoadTime[record.id_template] > 0"
              class="template_preview_container"
              :style="`background-image:url(${baseUrl + record.imageKey}.png&w=400&${imageLoadTime[record.id_template]})`"
            />
            <Button
              v-if="imageLoadTime[record.id_template] && imageLoadTime[record.id_template] > 0"
              icon="far fa-search-plus"
              :label="t('templateSelector.buttons.previewTemplate')"
              :loading="previewLoading[record.id_template]"
              class="p-button p-button-secondary preview-button"
              @click.stop="handlePreviewImageWithLoading(record)"
            />
            <div
              v-else
              class="template_preview_container"
              style="display: flex; justify-content: center; align-items: center;"
            >
              <ProgressSpinner
                style="width: 25px; height: 25px"
                stroke-width="8"
                fill="var(--surface-ground)"
                animation-duration="1.0s"
                aria-label="Loading template preview"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading More -->
      <div
        v-if="isListLoading && list.length > 0"
        class="loading-more"
      >
        <ProgressSpinner
          style="width: 30px; height: 30px"
          stroke-width="6"
          fill="var(--surface-ground)"
          animation-duration="1.0s"
          aria-label="Loading more templates"
        />
        <span class="ml-2">{{ t('templateSelector.buttons.loadMore') }}...</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';

// Extended interface to include name_marketing_workflow from GraphQL response
interface ExtendedTemplatesCustom extends TemplatesCustom {
  name_marketing_workflow?: string;
}

export default defineComponent({
  name: 'TemplateGridView',

  components: {
    Button,
    Loader,
    ProgressSpinner,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    list: {
      type: Array as PropType<ExtendedTemplatesCustom[]>,
      required: true,
    },

    isListLoading: {
      type: Boolean,
      required: true,
    },

    baseUrl: {
      type: String,
      required: true,
    },

    imageLoadTime: {
      type: Object as PropType<Record<number, number>>,
      required: true,
    },

    selectedTemplates: {
      type: Set as PropType<Set<number>>,
      required: true,
    },

    isElementPartTypeSection: {
      type: Boolean,
      required: true,
    },

    getSelectedSectionsForTemplate: {
      type: Function as PropType<(templateId: number) => string[]>,
      required: true,
    },
  },

  emits: ['handle-scroll', 'image-loaded', 'image-failed', 'preview-image', 'toggle-selection'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const scrollContainer = ref<HTMLElement>();
    const previewLoading = ref<Record<number, boolean>>({});

    const handleScroll = (event: Event) => {
      emit('handle-scroll', event);
    };

    const imageLoaded = (id: number) => {
      emit('image-loaded', id);
    };

    const imageFailed = (id: number) => {
      emit('image-failed', id);
    };

    const handlePreviewImageWithLoading = (record: ExtendedTemplatesCustom) => {
      previewLoading.value[record.id_template] = true;

      emit('preview-image', {
        id: record.id_template,
        templateType: record.type,
        templateLabel: record.label,
      }, () => {
        previewLoading.value[record.id_template] = false;
      });
    };

    const toggleSelection = (id: number) => {
      emit('toggle-selection', id);
    };

    return {
      t,
      scrollContainer,
      previewLoading,
      handleScroll,
      imageLoaded,
      imageFailed,
      handlePreviewImageWithLoading,
      toggleSelection,
    };
  },
});
</script>

<style scoped lang="scss">
$img-size: 12rem;

.grid-view {
  .loader-container {
    text-align: center;
    padding: 3rem;
  }

  .empty-message {
    text-align: center;
    padding: 3rem;

    i {
      display: block;
      margin-bottom: 1rem;
    }

    p {
      margin: 0;
      font-size: 1.1rem;
    }
  }

  .scroll-container {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 10px;
  }

  .loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    color: #6c757d;
  }
}

.preview-container-template {
  position: relative;
  border: 3px solid transparent;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  cursor: pointer;

  &.selected {
    border-color: #94c840;
  }

  .selection-checkbox {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 10;
  }

  .template-checkbox {
    display: none;
  }

  .checkbox-label {
    display: block;
    width: 24px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.9);
    border: 2px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      border-color: #94c840;
      background-color: rgba(148, 200, 64, 0.1);
    }

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 6px;
      width: 6px;
      height: 12px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
  }

  .template-checkbox:checked + .checkbox-label {
    background-color: #94c840;
    border-color: #94c840;

    &::after {
      opacity: 1;
    }
  }

  .template_preview_container {
    width: 100%;
    background-size: 100% auto;
    height: 300px;
    text-align: center;
    padding: 2em;
    background-position: center top;

    &:hover {
      webkit-animation: UPANDDOWN 10s infinite;
      -moz-animation: UPANDDOWN 10s infinite;
      -o-animation: UPANDDOWN 10s infinite;
      animation: UPANDDOWN 10s infinite;
    }
  }

  .preview-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
}

.template_preview_container:hover + .preview-button,
.preview-button:hover {
  opacity: 1;
}

.preview-label {
  min-height: 55px; // Fixed height to ensure consistent alignment
  display: flex;
  flex-direction: column;

  .template-title-container {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.2rem;
    line-height: 1.2;

    .template-source-icon {
      font-size: 1rem;
      flex-shrink: 0;
      margin-top: 1px; // Fine-tune vertical alignment
      color: #6c757d;
    }

    .template-title {
      font-weight: 600;
      color: #2c3e50;
      font-size: 0.9rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
      line-height: 1.2;
    }
  }

  .template-scenario-subtitle {
    font-size: 0.75rem;
    color: #6c757d;
    font-style: italic;
    margin-left: 1.25rem; // Align with title (icon width + gap)
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.1;
    margin-bottom: 0.25rem;
  }

  .selected-sections-indicator {
    font-size: 0.8rem;
    color: #94c840;
    margin-left: 1.25rem; // Align with title (icon width + gap)
    display: flex;
    align-items: center;
    margin-top: auto; // Push to bottom of container

    .sections-count {
      font-weight: 500;
    }

    i {
      font-size: 0.75rem;
    }
  }
}

.center {
  margin: 0 auto;
  display: block;
}
</style>
